import {
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  LocalPhone as PhoneIcon,
  School as SchoolIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import { useToast } from '../contexts/ToastContext';
import { useClientData } from '../hooks/useClientData';
import { clientRelationshipApi } from '../services/clientRelationshipApi';
import { Dietitian, dietitianApi } from '../services/dietitianApi';
import { disconnectionRequestApi } from '../services/disconnectionRequestApi';
import { ratingApi } from '../services/ratingApi';
import { colors } from '../theme';

const MyDietitianSettingsPage: React.FC = () => {
  const { client, loading } = useClientData({
    fetchAppointments: false,
    fetchDietPrograms: false,
    fetchDietitian: false,
  });
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dietitian, setDietitian] = useState<Dietitian | null>(null);
  const [loadingDietitian, setLoadingDietitian] = useState(false);
  const [disconnectionReason, setDisconnectionReason] = useState('');
  const [pendingDisconnection, setPendingDisconnection] = useState(false);
  const [myRatingScore, setMyRatingScore] = useState<number | null>(null);
  const [ratingSummary, setRatingSummary] = useState<{ averageScore: number; totalCount: number } | null>(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  const hasDietitian = client && (client.dietitianId || client.dietitianEmail);

  // Diyetisyen bilgilerini ve ayrılma durumunu yükle
  useEffect(() => {
    const loadDietitianData = async () => {
      if (!hasDietitian || !client) return;

      setLoadingDietitian(true);
      try {
        if (client.dietitianId) {
          const dietitianData = await dietitianApi.getDietitianById(
            client.dietitianId
          );
          setDietitian(dietitianData);

          // Bekleyen ayrılma isteği var mı kontrol et
          try {
            const relationship = await clientRelationshipApi.getRelationship(
              client.dietitianId,
              client.id
            );
            if (relationship?.status === 'PendingDisconnection') {
              setPendingDisconnection(true);
            }
          } catch (err) {
            // İlişki bulunamadı, normal
          }
        } else if (client.dietitianEmail) {
          const dietitianData = await dietitianApi.getDietitianByEmail(
            client.dietitianEmail
          );
          setDietitian(dietitianData);
        }
      } catch (error) {
        console.error('Diyetisyen bilgileri yüklenemedi:', error);
      } finally {
        setLoadingDietitian(false);
      }
    };

    loadDietitianData();
  }, [client, hasDietitian]);

  const loadRatingData = useCallback(async () => {
    const dietitianId = dietitian?.id || client?.dietitianId;
    if (!dietitianId || !client?.id) return;
    try {
      const [summary, myRating] = await Promise.all([
        ratingApi.getSummary(dietitianId),
        ratingApi.getMyRating(client.id, dietitianId),
      ]);
      setRatingSummary(summary);
      setMyRatingScore(myRating?.score ?? null);
    } catch {
      setRatingSummary(null);
      setMyRatingScore(null);
    }
  }, [dietitian?.id, client?.id, client?.dietitianId]);

  useEffect(() => {
    if (dietitian && client?.id) loadRatingData();
  }, [dietitian, client?.id, loadRatingData]);

  const handleRatingChange = async (_: React.SyntheticEvent, value: number | null) => {
    const dietitianId = dietitian?.id || client?.dietitianId;
    if (!client?.id || !dietitianId || value == null) return;
    try {
      setRatingSubmitting(true);
      await ratingApi.submitRating(client.id, dietitianId, value);
      setMyRatingScore(value);
      await loadRatingData();
      showSuccess('Puanınız kaydedildi.', 'Teşekkürler');
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Puan gönderilemedi.', 'Hata');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleRemoveDietitian = async () => {
    if (!client || !client.id || !client.dietitianId) {
      showError('Danışan bilgisi bulunamadı.', 'Hata');
      return;
    }

    try {
      setRemoving(true);
      setConfirmDialogOpen(false);

      // Ayrılma isteği oluştur
      await disconnectionRequestApi.createDisconnectionRequest({
        clientId: client.id,
        dietitianId: client.dietitianId,
        requestedBy: 'Client',
        reason: disconnectionReason.trim() || undefined,
      });

      showSuccess(
        'Ayrılma isteği gönderildi. Diyetisyeniniz 7 gün içinde onaylamazsa otomatik olarak ayrılacaksınız.',
        'Başarılı'
      );
      setPendingDisconnection(true);
      setDisconnectionReason('');

      // ClientLayout'u yenilemek için navigate kullan
      setTimeout(() => {
        navigate('/client/my-dietitian-settings');
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      console.error('Ayrılma isteği oluşturulamadı:', err);
      showError(
        err.message || 'Ayrılma isteği oluşturulurken bir hata oluştu.',
        'Hata'
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleSelectNewDietitian = () => {
    navigate('/client/select-dietitian');
  };

  const handleSendMessage = () => {
    navigate('/client/messages');
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Diyetisyen bilgileri yükleniyor..."
      />
    );
  }

  return (
    <Box>
      {hasDietitian ? (
        <>
          <Card
            elevation={3}
            sx={{
              mb: 3,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box
              sx={{
                background: colors.gradients.primary,
                p: 4,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    flex: 1,
                  }}
                >
                  <Avatar
                    src={dietitian?.profileImageUrl}
                    sx={{
                      width: 100,
                      height: 100,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '2rem',
                      border: '4px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      gutterBottom
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {dietitian
                        ? `${dietitian.firstName} ${dietitian.lastName}`
                        : client?.dietitianName || 'Diyetisyen'}
                    </Typography>
                    {dietitian?.specialization && (
                      <Chip
                        label={dietitian.specialization}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          fontWeight: 500,
                          mt: 0.5,
                          '& .MuiChip-label': {
                            px: 1.5,
                            fontSize: '0.875rem',
                          },
                        }}
                        icon={<SchoolIcon sx={{ color: 'white !important' }} />}
                      />
                    )}
                  </Box>
                </Box>
                <IconButton
                  onClick={handleSendMessage}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    width: 48,
                    height: 48,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  title="Mesaj Gönder"
                >
                  <MessageIcon />
                </IconButton>
              </Box>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {loadingDietitian ? (
                <Loading
                  size="large"
                  variant="wave"
                  mode="inline"
                  message="Diyetisyen bilgileri yükleniyor..."
                />
              ) : (
                <Grid container spacing={3}>
                  {/* İletişim Bilgileri */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                      sx={{
                        mb: 2,
                        color: colors.primary.main,
                        fontSize: '1rem',
                      }}
                    >
                      İletişim Bilgileri
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {dietitian?.email && (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <EmailIcon sx={{ color: colors.primary.main }} />
                          <Typography
                            variant="body1"
                            sx={{ fontSize: '0.9375rem' }}
                          >
                            {dietitian.email}
                          </Typography>
                        </Box>
                      )}
                      {dietitian?.phone && (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <PhoneIcon sx={{ color: colors.primary.main }} />
                          <Typography
                            variant="body1"
                            sx={{ fontSize: '0.9375rem' }}
                          >
                            {dietitian.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Hakkında */}
                  {dietitian?.bio && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                        sx={{
                          mb: 2,
                          color: colors.primary.main,
                          fontSize: '1rem',
                        }}
                      >
                        Hakkında
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7, fontSize: '0.9375rem' }}
                      >
                        {dietitian.bio}
                      </Typography>
                    </Grid>
                  )}

                  {/* Diyetisyeninizi Puanlayın */}
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        pt: 2,
                        mt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                        sx={{
                          mb: 1.5,
                          color: colors.primary.main,
                          fontSize: '1rem',
                        }}
                      >
                        Diyetisyeninizi Puanlayın
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Rating
                          name="dietitian-rating"
                          value={myRatingScore ?? 0}
                          onChange={handleRatingChange}
                          disabled={ratingSubmitting}
                          size="large"
                          icon={<StarIcon sx={{ color: colors.primary.main }} fontSize="inherit" />}
                          emptyIcon={<StarIcon sx={{ color: 'action.disabled' }} fontSize="inherit" />}
                        />
                        {ratingSummary != null && ratingSummary.totalCount > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Ortalama {ratingSummary.averageScore.toFixed(1)} ({ratingSummary.totalCount} oy)
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Oylar anonimdir. Kimin oy verdiği paylaşılmaz; yalnızca ortalama puan diyetisyenin ekranında gösterilir.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Ayrılma Seçeneği - En Altta, Göze Batmayacak Şekilde */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  {pendingDisconnection ? (
                    <>
                      <Typography
                        variant="body2"
                        color="warning.main"
                        gutterBottom
                        sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                      >
                        Ayrılma İsteği Beklemede
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.8125rem' }}
                      >
                        Diyetisyeniniz ayrılma isteğinizi onaylamadı. 7 gün içinde
                        onaylanmazsa otomatik olarak ayrılacaksınız.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Diyetisyeninizden ayrılmak istiyorsanız
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.8125rem' }}
                      >
                        Ayrılma isteği diyetisyeninize gönderilecek. Diyetisyeniniz
                        onaylamazsa 7 gün sonra otomatik olarak ayrılacaksınız.
                      </Typography>
                    </>
                  )}
                </Box>
                {!pendingDisconnection && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      removing ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <PersonRemoveIcon />
                      )
                    }
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={removing}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                      width: 280,
                      borderRadius: 1.5,
                      borderWidth: 1,
                      '&:hover': {
                        borderWidth: 1,
                      },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {removing ? 'Gönderiliyor...' : 'Ayrılma İsteği Gönder'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card
          elevation={3}
          sx={{
            mb: 3,
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: colors.primary.light,
                mb: 2,
              }}
            >
              <PersonAddIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
            <Typography
              variant="h5"
              fontWeight={600}
              gutterBottom
              sx={{ fontSize: '1.5rem' }}
            >
              Henüz Bir Diyetisyeniniz Yok
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, fontSize: '0.9375rem' }}
            >
              Size en uygun diyetisyeni seçerek başlayabilirsiniz.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleSelectNewDietitian}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: colors.gradients.primary,
                fontSize: '1rem',
                '&:hover': {
                  background: colors.gradients.primaryMedium,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(67, 160, 71, 0.4)',
                },
              }}
            >
              Diyetisyen Seç
            </Button>
          </Box>
        </Card>
      )}

      {/* Onay Dialog'u */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ fontSize: 28, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Diyetisyeninizden Ayrıl
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Diyetisyeninizden ayrılma isteği göndermek istediğinize emin misiniz?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Ayrılma Sebebi (Opsiyonel)"
            value={disconnectionReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisconnectionReason(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Diyetisyeninize iletmek istediğiniz sebep..."
          />
          <Box
            sx={{
              bgcolor: 'warning.light',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography
              variant="body2"
              color="warning.dark"
              fontWeight={500}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
            >
              <WarningIcon sx={{ fontSize: 20, mt: 0.25 }} />
              <span>
                Ayrılma isteği diyetisyeninize gönderilecek. Diyetisyeniniz 7 gün
                içinde onaylamazsa otomatik olarak ayrılacaksınız. Diyetisyeniniz
                onaylarsa hemen ayrılacaksınız.
              </span>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <CancelButton onClick={() => setConfirmDialogOpen(false)} />
          <Button
            onClick={handleRemoveDietitian}
            variant="contained"
            color="error"
            disabled={removing}
            startIcon={
              removing ? <CircularProgress size={20} /> : <PersonRemoveIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
          >
            {removing ? 'Gönderiliyor...' : 'Evet, İstek Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyDietitianSettingsPage;
